import React, { useState, useEffect } from "react";
import { X, Users, ChevronDown } from "lucide-react";
import axios from "axios";
import shiftsIcon from "../assets/shiftIcon.png";
import hoursIcon from "../assets/hoursIcon.png";
import companyIcon from "../assets/companyIcon.png";

const BASE_URL = process.env.REACT_APP_BASE_URL;

// Side-by-side stat, bigger icon, no background box around it — matches worker page's IconStat
const IconStat = ({ iconSrc, label, value }) => (
  <div className="flex-1 flex items-center gap-2.5 bg-white rounded-xl px-4 py-3.5">
    <img src={iconSrc} alt="" className="w-8 h-8 object-contain flex-shrink-0" />
    <div>
      <p className="text-lg font-bold text-gray-900 leading-tight">{value}</p>
      <p className="text-xs text-gray-500 leading-tight">{label}</p>
    </div>
  </div>
);

const WorkerSummaryModal = ({ isOpen, onClose, workerId }) => {
  const [summaryData, setSummaryData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [earningsExpanded, setEarningsExpanded] = useState(false);
  const [expandedCompanies, setExpandedCompanies] = useState(new Set());

  useEffect(() => {
    if (isOpen && workerId) {
      fetchPaymentSummary();
    }
  }, [isOpen, workerId]);

  const fetchPaymentSummary = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("superadmintoken");
      const response = await axios.post(
        `${BASE_URL}/lenskart-admin/workerPaymentSummary`,
        { workerId },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.data.success) {
        setSummaryData(response.data);
        // Auto-expand first company if data exists and is UTV member
        if (
          response.data.UTVMember &&
          response.data.companiesData &&
          response.data.companiesData.length > 0
        ) {
          setExpandedCompanies(new Set([response.data.companiesData[0].companyId]));
        }
      } else {
        console.error("Failed to fetch payment summary");
      }
    } catch (error) {
      console.error("Error fetching payment summary:", error);
    } finally {
      setLoading(false);
    }
  };

  const toggleCompany = (companyId) => {
    setExpandedCompanies((prev) => {
      const next = new Set(prev);
      if (next.has(companyId)) {
        next.delete(companyId);
      } else {
        next.add(companyId);
      }
      return next;
    });
  };

  if (!isOpen) return null;

  const isUTVMember = summaryData?.UTVMember || false;

  const totalEarnings = summaryData?.overallTotals?.totalEarnings || 0;

  const netEarnings = summaryData?.overallTotals?.totalNetPayment || 0;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 bg-[#E4E3FB] rounded-full flex items-center justify-center">
              <span className="text-indigo-700 text-xl font-bold">₹</span>
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">
                Ondemand Worker Payment Summary
              </h2>
              <p className="text-sm text-gray-500">
                {isUTVMember
                  ? "Detailed breakdown by company & department"
                  : "Overall work summary"}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="overflow-y-auto max-h-[calc(90vh-96px)]">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <div className="text-center">
                <div className="w-12 h-12 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin mx-auto mb-4"></div>
                <p className="text-gray-600">Loading payment details...</p>
              </div>
            </div>
          ) : !summaryData ? (
            <div className="text-center py-16">
              <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Users className="h-8 w-8 text-indigo-600" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                No Payment Data
              </h3>
              <p className="text-gray-600">
                No payment summary available for this worker.
              </p>
            </div>
          ) : (
            <div className="p-6">
              {/* Worker Details */}
              {/* {summaryData.workerDetails && (
                <div className="bg-white rounded-xl p-4 mb-6 border border-gray-100 shadow-sm">
                  <h3 className="text-base font-semibold text-gray-900 mb-3">
                    Worker Information
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <p className="text-gray-500">Name</p>
                      <p className="font-semibold">
                        {summaryData.workerDetails.workerName}
                      </p>
                    </div>
                    <div>
                      <p className="text-gray-500">Phone</p>
                      <p className="font-semibold">
                        {summaryData.workerDetails.workerPhone}
                      </p>
                    </div>
                  </div>
                </div>
              )} */}

              {/* Overall Summary — light purple card, same palette as worker page */}
              {isUTVMember && (
                <div className="bg-[#E4E3FB] rounded-2xl p-5 mb-8">
                  <h3 className="text-lg font-bold text-gray-900 mb-4">
                    Overall Summary
                  </h3>

                  {/* Hours / Shifts — matches mobile exactly (no companies box here) */}
                  <div className="flex gap-3 mb-4">
                    <IconStat
                      iconSrc={hoursIcon}
                      label="Shifts Hours"
                      value={summaryData.overallTotals?.totalHours?.toFixed(2) || "0.00"}
                    />
                    <IconStat
                      iconSrc={shiftsIcon}
                      label="Total Shifts"
                      value={summaryData.overallTotals?.totalShifts || 0}
                    />
                  </div>

                  {/* Total Earnings — expandable accordion, identical pattern & breakup to worker page */}
                  <div className="bg-white rounded-xl overflow-hidden">
                    <button
                      onClick={() => setEarningsExpanded((prev) => !prev)}
                      className="w-full flex items-center justify-between px-4 py-3.5"
                    >
                      <span className="text-sm text-gray-700">Total Earnings</span>
                      <div className="flex items-center gap-2">
                        <span className="text-base font-bold text-gray-900">
                          ₹{totalEarnings}
                        </span>
                        <ChevronDown
                          className={`h-4 w-4 text-gray-500 transition-transform ${
                            earningsExpanded ? "rotate-180" : ""
                          }`}
                        />
                      </div>
                    </button>

                    {earningsExpanded && summaryData.overallTotals && (
                      <div className="px-4 pb-4 pt-1 border-t border-gray-100">
                        <div className="space-y-2.5 mt-2">
                          <div className="flex items-center justify-between">
                            <span className="text-xs text-gray-600">Wage Earnings</span>
                            <span className="text-xs font-semibold text-gray-900">
                              ₹{summaryData.overallTotals.totalGrossAmount || 0}
                            </span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-xs text-gray-600">Incentive Earnings</span>
                            <span className="text-xs font-semibold text-green-600">
                              + ₹{summaryData.overallTotals.totalIncentive || 0}
                            </span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-xs text-gray-600">Referral Earnings</span>
                            <span className="text-xs font-semibold text-green-600">
                              + ₹{summaryData.overallTotals.totalReferral || 0}
                            </span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-xs text-gray-600">Deductions</span>
                            <span className="text-xs font-semibold text-red-500">
                              - ₹{summaryData.overallTotals.totalDeductedAmount || 0}
                            </span>
                          </div>
                          <div className="flex items-center justify-between pt-2 border-t border-gray-200 border-t-2">
                            <span className="text-sm font-semibold text-green-700">Net Earnings</span>
                            <span className="text-sm font-bold text-green-600">
                              ₹{netEarnings}
                            </span>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Company-wise Breakdown — Only for UTV Members */}
              {isUTVMember &&
                summaryData.companiesData &&
                summaryData.companiesData.length > 0 && (
                  <div>
                    <div className="flex items-center gap-2 mb-4">
                      <img src={companyIcon} alt="" className="w-5 h-5 object-contain" />
                      <h3 className="text-lg font-bold text-gray-900">
                        Company-wise Breakdown
                      </h3>
                      <span className="text-xs font-semibold text-indigo-700 bg-indigo-100 px-2 py-0.5 rounded-full">
                        {summaryData.companiesData.length}
                      </span>
                    </div>

                    <div className="space-y-4">
                      {summaryData.companiesData.map((company) => {
                        const isExpanded = expandedCompanies.has(company.companyId);
                        return (
                          <div
                            key={company.companyId}
                            className="rounded-2xl border border-gray-100 shadow-sm overflow-hidden"
                          >
                            <button
                              onClick={() => toggleCompany(company.companyId)}
                              className="w-full flex items-center gap-3 px-5 py-4 hover:bg-gray-50 transition-colors"
                            >
                              {company.companyLogo ? (
                                <img
                                  src={company.companyLogo}
                                  alt={company.companyName}
                                  className="w-10 h-10 rounded-full object-contain border border-gray-200 bg-white p-1 flex-shrink-0"
                                  onError={(e) => {
                                    e.target.style.display = "none";
                                    e.target.nextSibling.style.display = "flex";
                                  }}
                                />
                              ) : (
                                <div className="w-10 h-10 bg-indigo-100 rounded-full flex items-center justify-center border border-gray-200 flex-shrink-0">
                                  <Users className="h-5 w-5 text-indigo-600" />
                                </div>
                              )}

                              <div className="flex-1 text-left">
                                <h4 className="font-bold text-gray-900 text-base">
                                  {company.companyName}
                                </h4>
                                <p className="text-xs text-gray-500">
                                  {company.companyTotalShifts} shifts ·{" "}
                                  {company.companyTotalHours.toFixed(2)} hrs
                                </p>
                              </div>

                              <div className="text-right">
                                <p className="text-base font-bold text-green-600">
                                  ₹{company.companyTotalGrossAmount}
                                </p>
                              </div>

                              <ChevronDown
                                className={`h-4 w-4 text-gray-400 flex-shrink-0 transition-transform ${
                                  isExpanded ? "rotate-180" : ""
                                }`}
                              />
                            </button>

                            {isExpanded && (
                              <div className="border-t border-gray-100">
                                <div className="bg-[#E7F7EC] px-5 py-4">
                                  <div className="grid grid-cols-3 gap-2 max-w-md">
                                    <div className="text-center">
                                      <p className="text-lg font-bold text-gray-900">
                                        {company.companyTotalHours.toFixed(2)}
                                      </p>
                                      <p className="text-xs text-gray-600">Total Hours</p>
                                    </div>
                                    <div className="text-center">
                                      <p className="text-lg font-bold text-gray-900">
                                        {company.companyTotalShifts}
                                      </p>
                                      <p className="text-xs text-gray-600">Total Shifts</p>
                                    </div>
                                    <div className="text-center">
                                      <p className="text-lg font-bold text-green-600">
                                        ₹{company.companyTotalGrossAmount}
                                      </p>
                                      <p className="text-xs text-gray-600">Gross Earnings</p>
                                    </div>
                                  </div>
                                </div>

                                <div className="bg-white">
                                  {/* Desktop: departments laid out as a grid of cards instead of a stacked list */}
                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 p-5">
                                    {company.departments.map((dept, deptIndex) => (
                                      <div
                                        key={deptIndex}
                                        className="rounded-xl border border-gray-100 overflow-hidden"
                                      >
                                        <div className="bg-gray-200 px-4 py-2.5 text-center">
                                          <p className="text-sm font-semibold text-gray-800">
                                            {dept.department || "General Department"}
                                          </p>
                                        </div>
                                        <div className="px-4 py-4">
                                          <div className="grid grid-cols-4 gap-2">
                                            <div className="text-center">
                                              <p className="text-base font-bold text-gray-900">
                                                {dept.totalWorkingHours.toFixed(2)}
                                              </p>
                                              <p className="text-xs text-gray-500">Hours</p>
                                            </div>
                                            <div className="text-center">
                                              <p className="text-base font-bold text-gray-900">
                                                {dept.totalShifts}
                                              </p>
                                              <p className="text-xs text-gray-500">Shifts</p>
                                            </div>
                                            <div className="text-center">
                                              <p className="text-base font-bold text-green-600">
                                                ₹{dept.totalGrossAmount}
                                              </p>
                                              <p className="text-xs text-gray-500">Earnings</p>
                                            </div>
                                            <div className="text-center">
                                              <p className="text-base font-bold text-blue-600">
                                                ₹{dept.totalNetPayment}
                                              </p>
                                              <p className="text-xs text-gray-500">Net</p>
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

              {/* Single Company Display for Non-UTV Members — always expanded, same card language */}
              {!isUTVMember &&
                summaryData.companiesData &&
                summaryData.companiesData.length > 0 && (
                  <div>
                    <div className="flex items-center gap-2 mb-4">
                      <img src={companyIcon} alt="" className="w-5 h-5 object-contain" />
                      <h3 className="text-lg font-bold text-gray-900">Company Details</h3>
                    </div>

                    <div className="space-y-4">
                      {summaryData.companiesData.map((company) => (
                        <div
                          key={company.companyId}
                          className="rounded-2xl border border-gray-100 shadow-sm overflow-hidden"
                        >
                          <div className="flex items-center gap-3 px-5 py-4">
                            {company.companyLogo ? (
                              <img
                                src={company.companyLogo}
                                alt={company.companyName}
                                className="w-10 h-10 rounded-full object-contain border border-gray-200 bg-white p-1 flex-shrink-0"
                                onError={(e) => {
                                  e.target.style.display = "none";
                                  e.target.nextSibling.style.display = "flex";
                                }}
                              />
                            ) : (
                              <div className="w-10 h-10 bg-indigo-100 rounded-full flex items-center justify-center border border-gray-200 flex-shrink-0">
                                <Users className="h-5 w-5 text-indigo-600" />
                              </div>
                            )}
                            <div className="text-left">
                              <h4 className="font-bold text-gray-900 text-base">
                                {company.companyName}
                              </h4>
                              <p className="text-xs text-gray-500">
                                {company.companyTotalShifts} shifts ·{" "}
                                {company.companyTotalHours.toFixed(2)} hrs
                              </p>
                            </div>
                          </div>

                          <div className="border-t border-gray-100">
                            <div className="bg-[#E7F7EC] px-5 py-4">
                              <div className="grid grid-cols-2 gap-2 max-w-xs">
                                <div className="text-center">
                                  <p className="text-lg font-bold text-gray-900">
                                    {company.companyTotalHours.toFixed(2)}
                                  </p>
                                  <p className="text-xs text-gray-600">Total Hours</p>
                                </div>
                                <div className="text-center">
                                  <p className="text-lg font-bold text-gray-900">
                                    {company.companyTotalShifts}
                                  </p>
                                  <p className="text-xs text-gray-600">Total Shifts</p>
                                </div>
                              </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 p-5 bg-white">
                              {company.departments.map((dept, deptIndex) => (
                                <div
                                  key={deptIndex}
                                  className="rounded-xl border border-gray-100 overflow-hidden"
                                >
                                  <div className="bg-gray-200 px-4 py-2.5 text-center">
                                    <p className="text-sm font-semibold text-gray-800">
                                      {dept.department || "General Department"}
                                    </p>
                                  </div>
                                  <div className="px-4 py-4">
                                    <div className="grid grid-cols-2 gap-2">
                                      <div className="text-center">
                                        <p className="text-base font-bold text-gray-900">
                                          {dept.totalWorkingHours.toFixed(2)}
                                        </p>
                                        <p className="text-xs text-gray-500">Hours</p>
                                      </div>
                                      <div className="text-center">
                                        <p className="text-base font-bold text-gray-900">
                                          {dept.totalShifts}
                                        </p>
                                        <p className="text-xs text-gray-500">Shifts</p>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default WorkerSummaryModal;